import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';

import { Chip, ListItemButton, Paper } from '@mui/material';

import routeIcon from '@/shared/configs/routeIcon';
import { AppPath } from '@/shared/constants/path';
import useAuth from '@/shared/contexts/auth/hooks/useAuth';
import NavigationItem from '@/shared/types/navigationItem.type';

import CurrentUserSettingsButton from './CurrentUserSettingsButton';

const navigationListItems: NavigationItem[] = [
  {
    label: 'accountSettings:userSettings',
    href: AppPath.Settings_UserSettings,
    hidden: true,
  },
  {
    label: 'accountSettings:fulfilmentProviders',
    href: AppPath.Settings_FulfilmentProviders,
  },
  {
    label: 'accountSettings:shippingProviders',
    href: AppPath.Settings_ShippingProviders,
  },
  {
    label: 'accountSettings:paymentProcessors',
    href: AppPath.Settings_PaymentProcessors,
  },
  {
    label: 'accountSettings:usersManagement',
    href: AppPath.Settings_UsersManagement,
  },
];

type NavigationListProps = {
  getActiveRoute: (activeNavigationItem: NavigationItem) => void;
};

const NavigationList: FC<NavigationListProps> = ({ getActiveRoute }) => {
  const { t } = useTranslation(['accountSettings']);
  const { user } = useAuth();

  const location = useLocation();
  let activeItem: NavigationItem | null = null;

  for (const item of navigationListItems) {
    if (location.pathname.includes(item.href)) {
      activeItem = item;
    }
  }

  useEffect(() => {
    if (activeItem) {
      getActiveRoute(activeItem);
    }
  }, [activeItem, getActiveRoute]);

  return (
    <Paper sx={{ p: 2 }}>
      {user && (
        <Chip
          label={user.user_level}
          color='primary'
          sx={{ mb: 1, textTransform: 'lowercase' }}
          size='small'
        />
      )}

      {user && (
        <CurrentUserSettingsButton
          user={user}
          selected={location.pathname.includes(AppPath.Settings_UserSettings)}
        />
      )}

      {navigationListItems.map((item, index) => {
        const regex = new RegExp(`(^|/)${item.href}(/|$)`);
        const selected = regex.test(window.location.pathname);

        if (item.hidden) return null;

        return (
          <ListItemButton
            key={index}
            component={NavLink}
            to={item.href}
            selected={selected}
            sx={{
              'borderRadius': 4,
              'gap': 1,
              '&.Mui-selected': {
                color: selected ? 'primary.main' : 'text.secondary',
              },
            }}
          >
            {routeIcon[item.href]?.renderIcon({
              color: selected ? 'primary.main' : 'text.secondary',
            })}
            {t(item.label)}
          </ListItemButton>
        );
      })}
    </Paper>
  );
};

export default NavigationList;
